import React, { useRef, useState, useEffect } from 'react'
import type { Theme } from '@material-ui/core'
import { Grid, makeStyles } from '@material-ui/core'
import RemoveIcon from '@material-ui/icons/RemoveCircle'
import EditIcon from '@material-ui/icons/Edit'
import type { QuoteDocument, QuoteFile, QuoteSource } from 'paintscout'
import {
  Dialog,
  DialogActions,
  DialogToggles,
  DialogContent,
  DialogTitle,
  Button,
  DialogActionArrows,
  useClientOptions,
  useDialogs,
  FormSection,
  ConfirmationDialog,
  FormSectionTitle,
  InputField,
  OverridableInputField,
  OverridableTextArea,
  Typography,
  CompanyCamLogo
} from '@paintscout/ui'
import type { QuoteItemSection, UpdateableGroup, RenderableItem } from '@paintscout/util/builder'
import {
  createGroup,
  deleteGroup,
  getFilesForItem,
  getGroup,
  getItem,
  getItems,
  removeItemsFromGroup,
  updateFilesForItem,
  updateGroup,
  getFeatures,
  updateItem,
  hasIntegrationInterface
} from '@paintscout/util/builder'
import MediaIcon from '@material-ui/icons/PermMedia'
import { Detector } from 'react-detect-offline'
import get from 'lodash/get'
import { calculate } from '@paintscout/util/calculator'
import { isLooselyEqual } from '@paintscout/util'
import FilesGrid from '../../FilesGrid'
import ItemTable, { ItemColumn, AmountColumn, ItemTableCell } from '../../ItemTable'
import ActionColumn from '../../ItemTable/columns/ActionColumn'
import EditItemFilesDialog from '../EditItemFilesDialog'

import EditAreaDialog from '../EditAreaDialog'
import EditSubstrateDialog from '../EditSubstrateDialog'
import EditLineItemDialog from '../EditLineItemDialog'

import EditIntegrationItemFilesDialog from '../EditIntegrationItemFilesDialog'

export interface EditGroupDialogProps {
  quote: QuoteDocument
  item?: UpdateableGroup
  keys?: string[]
  section?: QuoteItemSection
  addItems?: boolean
  onConfirm: (quote: QuoteDocument, itemKey?: string) => void
  onCancel: (quote: QuoteDocument, isDirty: boolean) => void
  nextItemKey?: string
  previousItemKey?: string
}

const useStyles = makeStyles<Theme, EditGroupDialogProps>(
  (theme) => ({
    root: {},
    content: {},
    input: {
      '& input': {
        fontWeight: theme.typography.fontWeightMedium
      }
    },
    contentInner: {},
    htmlEditor: {}
  }),
  { name: 'EditGroupDialog' }
)

function EditGroupDialog(props: EditGroupDialogProps) {
  const classes = useStyles(props)
  const {
    keys,
    item,
    section,
    quote: propsQuote,
    addItems = false,
    onConfirm,
    onCancel,
    nextItemKey,
    previousItemKey,
    ...baseDialogProps
  } = props
  const { openDialog, dismissDialog } = useDialogs()
  const { options } = useClientOptions()

  const isNewGroup = !item

  const [group, setGroup] = useState(
    isNewGroup
      ? createGroup({
          quote: propsQuote,
          clientOptions: options,
          keys,
          section
        })
      : getGroup({ quote: propsQuote, key: item.key })
  )
  const originalGroup = useRef(group)
  const [quote, setQuote] = useState(
    isNewGroup ? updateItem({ quote: propsQuote, options, item: group, section, calculate: true }) : propsQuote
  )

  const itemKey = group.key

  const [files, setFiles] = useState(getFilesForItem({ quote, options, itemKey }))

  const features = getFeatures({ options })
  const mediaFeature = get(features, 'media.enabled', true)

  const [items, setItems] = useState(
    getItems({
      quote,
      options,
      view: group.view,
      keys: group.children,
      section: group.section,
      consumer: 'customer',
      trimBySubstrateSection: group.section,
      showGroups: true,
      groupKey: group.key
    })
  )

  const showFiles = files.length > 0

  const descriptionRef = useRef(null)
  const priceRef = useRef(null)
  const labelRef = useRef(null)

  const handleNavArrowClick = (navItemKey: string) => {
    onConfirm(getUpdatedQuote(), navItemKey)
  }

  function getUpdatedQuote() {
    const updatedGroup = syncedGroup()
    return updateGroup({
      quote,
      options,
      group: updatedGroup,
      section: updatedGroup.section
    })
  }

  function syncedGroup(): UpdateableGroup {
    const label = labelRef.current?.value ?? group.label
    const description = descriptionRef.current?.getValue() ?? group.description
    const price = priceRef.current?.getValue() ?? group.price

    const updatedGroup: UpdateableGroup = {
      ...group,
      label,
      description,
      price
    }

    return updatedGroup
  }

  useEffect(() => {
    if (item?.key) {
      setQuote(propsQuote)
      const updatedGroup = getGroup({ quote: propsQuote, key: item.key })
      if (descriptionRef?.current) {
        descriptionRef.current.setValue(
          updatedGroup.description.useCustom ? updatedGroup.description.custom : updatedGroup.description.default
        )
      }
      if (priceRef?.current) {
        priceRef.current.setValue(updatedGroup.price)
      }
      if (labelRef?.current) {
        labelRef.current.value = updatedGroup.label
      }
      const updatedFiles = getFilesForItem({ quote: propsQuote, options, itemKey: item.key })
      setFiles(updatedFiles)
      setGroup(updatedGroup)
      setItems(
        getItems({
          quote: propsQuote,
          options,
          view: updatedGroup.view,
          keys: updatedGroup.children,
          section: updatedGroup.section,
          consumer: 'customer',
          trimBySubstrateSection: updatedGroup.section,
          showGroups: true,
          groupKey: updatedGroup.key
        })
      )
    }
  }, [propsQuote, item, item?.key])

  return (
    <Dialog open={true} maxWidth="lg" {...baseDialogProps}>
      <DialogTitle>Edit Group</DialogTitle>
      <DialogContent classes={{ root: classes.content }}>
        <form
          id="edit-group-form"
          onSubmit={(e) => {
            e.preventDefault()
            handleDialogConfirm(e)
          }}
          onKeyDown={(keyEvent) => {
            if ((keyEvent.charCode || keyEvent.keyCode) === 13 && !(keyEvent.target as any).editor) {
              handleDialogConfirm(keyEvent)
            }
          }}
        >
          <div className={classes.contentInner}>
            <Grid container={true} direction="column" spacing={3}>
              <Grid item={true}>
                <InputField
                  label={'Name'}
                  defaultValue={group.label}
                  required={true}
                  error={!group.label}
                  name={'label'}
                  fullWidth={true}
                  classes={{ root: classes.input }}
                  inputRef={labelRef}
                />
              </Grid>
              <Grid item={true}>
                <OverridableTextArea
                  label={'Description'}
                  required={true}
                  allowFormatting
                  value={group.description}
                  classes={{ htmlEditor: classes.htmlEditor }}
                  ref={descriptionRef}
                />
              </Grid>
              <Grid item={true}>
                <OverridableInputField
                  label={'Price'}
                  value={group.price}
                  resetBlank={true}
                  format={'price'}
                  autoSelect={true}
                  ref={priceRef}
                />
              </Grid>
              <Grid item={true}>
                <FormSectionTitle title="Items" />
                <ItemTable
                  items={items}
                  showAddButton={false}
                  draggable={false}
                  checkboxes={false}
                  renderHeadColumns={() => (
                    <>
                      <ItemTableCell>Item</ItemTableCell>
                      <ItemTableCell align="right">
                        {/* <AmountHeadColumn isEditable={false} consumer="crew" disabled /> */}
                      </ItemTableCell>
                      <ItemTableCell align="right">
                        {/* <AmountHeadColumn isEditable={false} consumer="customer" disabled /> */}
                      </ItemTableCell>

                      {/* action column */}
                      <ItemTableCell />
                    </>
                  )}
                  renderRowColumns={(item: RenderableItem) => {
                    return (
                      <>
                        <ItemTableCell>
                          <ItemColumn item={item} consumer={'customer'} isEditable section={group.section} />
                        </ItemTableCell>
                        <ItemTableCell align="right">
                          <AmountColumn
                            item={{
                              ...item,
                              value: (item?.additionalFields?.painting ?? 0) + (item?.additionalFields?.prep ?? 0)
                            }}
                            consumer={'crew'}
                            showBackup
                          />
                        </ItemTableCell>
                        <ItemTableCell align="right">
                          <AmountColumn item={item} consumer={'customer'} showBackup />
                        </ItemTableCell>
                        <ItemTableCell cancelDrag isControl>
                          <ActionColumn
                            onActionClick={(ev, action) => {
                              if (action === 'remove') {
                                handleRemoveItem(item.key)
                              } else if (action === 'edit') {
                                handleEditItem(item.key)
                              }
                            }}
                            actions={[
                              { key: 'edit', label: 'Edit', icon: EditIcon },
                              { key: 'remove', label: 'Remove from Group', icon: RemoveIcon }
                            ]}
                          />
                        </ItemTableCell>
                      </>
                    )
                  }}
                />
              </Grid>
              <Grid item={true}>
                {showFiles && (
                  <FormSection hideDivider>
                    <Typography variant="h4">Media</Typography>
                    <FilesGrid
                      files={files}
                      editable={true}
                      onReorder={handleFilesReorder}
                      showUploadDropzone={false}
                      onFileClick={(index) => handleFileClick(files, index, null, 'device')}
                    />
                  </FormSection>
                )}
              </Grid>
            </Grid>
          </div>
        </form>
      </DialogContent>
      {mediaFeature && (
        <Detector>
          {(online) => (
            <DialogToggles
              toggles={[
                {
                  key: 'showMedia',
                  label: 'Add Media',
                  icon: MediaIcon,
                  disabled: !online,
                  subItems: hasIntegrationInterface({ options, integration: 'companycam' })
                    ? [
                        {
                          key: 'companycam',
                          label: 'CompanyCam',
                          value: true,
                          icon: CompanyCamLogo
                        },
                        {
                          key: 'device',
                          label: 'My Device',
                          value: true,
                          icon: MediaIcon
                        }
                      ]
                    : undefined
                }
              ]}
              onToggle={handleToggleSection}
              scroll
            />
          )}
        </Detector>
      )}
      <DialogActions
        leftButton={
          <Button onClick={handleCancel} variant="textNoPadding">
            Cancel
          </Button>
        }
        arrows={
          <DialogActionArrows
            hidden={isNewGroup || (!previousItemKey && !nextItemKey)}
            onPrevious={previousItemKey ? () => handleNavArrowClick(previousItemKey) : null}
            onNext={nextItemKey ? () => handleNavArrowClick(nextItemKey) : null}
          />
        }
      >
        {item && group.children.length > 0 && !addItems && (
          <Button variant={'text'} onClick={handleDialogDelete}>
            Ungroup Items
          </Button>
        )}
        <Button type="submit" variant={'contained'} form="edit-group-form">
          {isNewGroup ? 'Add Group' : 'Done'}
        </Button>
      </DialogActions>
    </Dialog>
  )

  function handleCancel() {
    const isDirtyCheck =
      !isLooselyEqual(originalGroup.current, group) ||
      !isLooselyEqual({ label: labelRef.current?.value }, { label: group.label }) ||
      !isLooselyEqual(descriptionRef.current.getValue(), group.description) ||
      !isLooselyEqual(priceRef.current.getValue(), group.price)

    const originalQuote = updateGroup({
      quote: propsQuote,
      options,
      group: originalGroup.current,
      section: originalGroup.current.section
    })

    onCancel(originalQuote, isDirtyCheck)
  }

  function handleRemoveItem(key: string) {
    const currentGroup = syncedGroup()
    const updatedQuote = removeItemsFromGroup({ quote, options, group: currentGroup, keys: [key] })
    const updatedGroup = getGroup({ quote: updatedQuote, key: itemKey })

    // Retian custom price & description if they have been set
    const currentPriceValue = priceRef.current.getValue()
    if (currentPriceValue.useCustom) {
      updatedGroup.price = currentPriceValue
    }
    const currentDescriptionVal = descriptionRef.current.getValue()
    if (currentDescriptionVal.useCustom) {
      updatedGroup.description = currentDescriptionVal
    } else {
      descriptionRef.current.setValue(
        updatedGroup.description.custom ? updatedGroup.description.custom : updatedGroup.description.default
      )
    }

    setQuote(updatedQuote)
    setGroup(updatedGroup)

    // Update items
    setItems(
      getItems({
        quote: updatedQuote,
        options,
        view: updatedGroup.view,
        keys: updatedGroup.children,
        section: updatedGroup.section,
        consumer: 'customer',
        trimBySubstrateSection: updatedGroup.section,
        showGroups: true,
        groupKey: updatedGroup.key
      })
    )
  }

  function handleEditItem(key: string) {
    const item = getItem({ quote, options, key })
    const group = getGroup({ quote, key: itemKey })

    let EditDialog
    if (item.type === 'area') {
      EditDialog = EditAreaDialog
    } else if (item.type === 'substrate') {
      EditDialog = EditSubstrateDialog
    } else if (item.type === 'lineItem') {
      EditDialog = EditLineItemDialog
    }

    return openDialog(EditDialog, {
      quote,
      item,
      section: group.section,
      isInGroup: true,
      onConfirm: (newQuote: QuoteDocument) => {
        const newGroup = newQuote.groups[itemKey]
        const updatedGroup = updateGroup({
          quote: newQuote,
          options,
          group: newGroup,
          section: props.section
        })
        const updatedQuote = calculate(updatedGroup, options)
        const calculatedGroup = getGroup({ quote: updatedQuote, key: itemKey })
        setQuote(updatedQuote)
        setGroup(calculatedGroup)
        setItems(
          getItems({
            quote: updatedQuote,
            options,
            view: calculatedGroup.view,
            keys: calculatedGroup.children,
            section: calculatedGroup.section,
            consumer: 'customer',
            trimBySubstrateSection: calculatedGroup.section,
            showGroups: true,
            groupKey: calculatedGroup.key
          })
        )
        const updatedFiles = getFilesForItem({ quote: updatedQuote, options, itemKey })
        setFiles(updatedFiles)
        dismissDialog()
      },
      onCancel: dismissDialog,
      cancelButtonLabel: 'Back'
    } as React.ComponentProps<typeof EditDialog>)
  }

  function handleFilesReorder(files: QuoteFile[]) {
    setQuote(updateFilesForItem({ quote, options, itemKey, files }))
  }

  function handleDialogDelete() {
    openDialog(ConfirmationDialog, {
      message: 'Are you sure you want to ungroup these items?',
      onConfirm: () => {
        dismissDialog()

        const updatedQuote = deleteGroup({ quote, options, key: itemKey })
        onConfirm?.(updatedQuote)
      },
      onCancel: dismissDialog
    })
  }

  function handleFileClick(files?: QuoteFile[], index?: number, uploadFiles?: File[], key?: string) {
    if (key === 'showMedia' || key === 'device' || !key) {
      openDialog(EditItemFilesDialog, {
        files,
        index,
        onDelete: (_key, currentFiles) => {
          const updatedQuote = updateFilesForItem({ quote, options, itemKey: itemKey, files: currentFiles })
          const updatedGroup = getGroup({ quote: updatedQuote, key: itemKey })

          setQuote(updatedQuote)
          setGroup(updatedGroup)
          setFiles(currentFiles)
        },
        onConfirm: (newFiles) => {
          const updatedQuote = updateFilesForItem({
            quote,
            itemKey: itemKey,
            options,
            files: newFiles,
            append: false
          })
          const updatedGroup = getGroup({ quote: updatedQuote, key: itemKey })
          setFiles(newFiles)
          setQuote(updatedQuote)
          setGroup(updatedGroup)
          dismissDialog()
        },
        onCancel: dismissDialog
      })
    } else {
      openDialog(EditIntegrationItemFilesDialog, {
        provider: key,
        files,
        source: quote?.sources?.[key],
        onConfirm: (newFiles, deletedFiles, source) => {
          handleFilesChange(newFiles, source, true)

          dismissDialog()
        },
        onCancel: dismissDialog
      })
    }
  }

  function handleFilesChange(files: QuoteFile[], source?: QuoteSource, append?: boolean) {
    const updatedQuote = updateFilesForItem({
      quote,
      options,
      itemKey,
      files,
      append: typeof append === 'undefined' ? true : append
    })

    if (source) {
      updatedQuote.sources = {
        ...updatedQuote.sources,
        [source.provider]: source
      }
    }
    const updatedGroup = getGroup({ quote: updatedQuote, key: itemKey })

    setGroup(updatedGroup)
    setQuote(updatedQuote)
  }

  function handleToggleSection(key: string, _value: boolean) {
    if (key === 'showMedia' || key === 'device' || key === 'companycam') {
      const files = getFilesForItem({ quote, options, itemKey })
      handleFileClick(files, files.length, null, key)
    }
  }

  function handleDialogConfirm(_event: any) {
    if (group.children.length === 0) {
      openDialog(ConfirmationDialog, {
        message: 'This group will be deleted because it contains no items. Are you sure?',
        onConfirm: () => {
          dismissDialog()

          const updatedQuote = deleteGroup({ quote, options, key: itemKey })
          onConfirm?.(updatedQuote)
        },
        onCancel: dismissDialog
      })
    } else {
      const updatedGroup = syncedGroup()
      const updatedQuote = updateGroup({
        quote,
        options,
        group: updatedGroup,
        calculate: true,
        section
      })
      onConfirm?.(updatedQuote)
    }
  }
}

export default EditGroupDialog
